// UK double-ring ringback for the Camera Off dialler — HTMLAudioElement
// implementation.
//
// Why not Web Audio oscillators: on-device diagnostics (2026-10) showed
// AudioContext oscillators produce NO audible output on iPhone (Safari AND
// Chrome — both WebKit) even with a gesture-unlocked, "running" context,
// because Web Audio renders through the ambient AVAudioSession category
// (silenced by the hardware mute switch / subject to routing quirks). An
// HTMLAudioElement uses the playback category and is audible.
//
// The tone itself is IDENTICAL to the oscillator version: 400+450 Hz dual
// sine, 0.4s on / 0.2s off / 0.4s on, then a 2s pause — synthesised once as
// a 3-second WAV (data URI) and looped. play() is triggered synchronously
// from the "Start Matching" tap (iOS media-element gesture requirement);
// startRingback()/stopRingback() keep the same API as before so PreCall and
// Matchmaking are unchanged.

const SR = 22050;
const CYCLE_SECONDS = 3.0; // 1.0s of ringing + 2.0s pause
let ringUrl = null;
let el = null;
let playing = false;
let retryListener = null;

// iOS (Safari 17+): guarantee the shared audio session stays compatible
// with microphone capture. A media element that has played can leave the
// session in a playback-only state, so iOS refuses the NEXT getUserMedia
// with "AudioSession category is not compatible with audio capture" (seen
// on iPhone when re-entering the Camera Off pre-call after cancelling a
// search). "play-and-record" permits capture AND playback and — like
// "playback" — is not silenced by the hardware mute switch. Asserted
// before every mic capture and whenever the ring stops.
export const prepareAudioSessionForCapture = () => {
  if ("audioSession" in navigator) {
    try {
      navigator.audioSession.type = "play-and-record";
    } catch {
      /* older WebKit without the Audio Session API */
    }
  }
};

const buildRingWavUrl = () => {
  if (ringUrl) return ringUrl;
  const total = Math.round(SR * CYCLE_SECONDS);
  const pcm = new Int16Array(total);
  const fade = Math.round(0.008 * SR); // 8ms edge fades prevent clicks
  for (let i = 0; i < total; i++) {
    const t = i / SR;
    let env = 0;
    if (t < 0.4) {
      env = Math.min(1, i / fade, (0.4 * SR - i) / fade);
    } else if (t >= 0.6 && t < 1.0) {
      env = Math.min(1, (i - 0.6 * SR) / fade, (1.0 * SR - i) / fade);
    }
    if (env > 0) {
      const v =
        (Math.sin(2 * Math.PI * 400 * t) + Math.sin(2 * Math.PI * 450 * t)) *
        0.5;
      pcm[i] = Math.round(v * env * 0.55 * 32767);
    }
  }
  const header = new ArrayBuffer(44);
  const dv = new DataView(header);
  const wstr = (off, s) =>
    s.split("").forEach((c, j) => dv.setUint8(off + j, c.charCodeAt(0)));
  wstr(0, "RIFF");
  dv.setUint32(4, 36 + pcm.length * 2, true);
  wstr(8, "WAVEfmt ");
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true); // PCM
  dv.setUint16(22, 1, true); // mono
  dv.setUint32(24, SR, true);
  dv.setUint32(28, SR * 2, true);
  dv.setUint16(32, 2, true);
  dv.setUint16(34, 16, true);
  wstr(36, "data");
  dv.setUint32(40, pcm.length * 2, true);
  const bytes = new Uint8Array(44 + pcm.length * 2);
  bytes.set(new Uint8Array(header), 0);
  bytes.set(new Uint8Array(pcm.buffer), 44);
  let bin = "";
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  ringUrl = "data:audio/wav;base64," + btoa(bin);
  return ringUrl;
};

const getEl = () => {
  if (!el) {
    el = new Audio(buildRingWavUrl());
    el.loop = true; // 3s cycle already contains the 2s pause
    el.preload = "auto";
    el.volume = 1.0; // loudness is baked into the WAV (0.55 amplitude)
    // Play-and-record (not plain "playback") so the ring is never silenced
    // by the mute switch yet mic capture keeps working afterwards.
    prepareAudioSessionForCapture();
  }
  return el;
};

const tryPlay = () => {
  const a = getEl();
  const p = a.play();
  if (p && p.catch) {
    p.catch(() => {
      // No usable gesture yet (e.g. page refreshed mid-search) — retry on
      // the user's next touch anywhere.
      if (!retryListener) {
        retryListener = () => {
          if (playing) a.play().catch(() => {});
        };
        window.addEventListener("pointerdown", retryListener);
        window.addEventListener("touchend", retryListener);
      }
    });
  }
};

// Called synchronously from the "Start Matching" tap: iOS requires media
// playback to begin inside a user gesture, so the ring starts here (exactly
// like a real phone starts ringing the moment you dial).
export const unlockRingbackAudio = () => {
  startRingback();
};

export const startRingback = () => {
  if (playing) return; // idempotent — StrictMode double-mount safe
  playing = true;
  tryPlay();
};

export const stopRingback = () => {
  playing = false;
  if (retryListener) {
    window.removeEventListener("pointerdown", retryListener);
    window.removeEventListener("touchend", retryListener);
    retryListener = null;
  }
  if (el) {
    el.pause(); // immediate silence
    // Fully release the element's hold on the shared audio session so a
    // cancelled search cannot leave it pinned in a playback-only state.
    // The ring is always re-armed by the next Start Matching tap (a real
    // user gesture), so unloading here never violates iOS autoplay rules.
    try {
      el.load();
    } catch {
      /* not loaded yet — harmless */
    }
  }
  // Defensive: never hand a playback-only session to the next mic check.
  prepareAudioSessionForCapture();
};
